import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Store as StoreIcon } from "@mui/icons-material";
import taskerService, {
  type TaskerTask,
} from "../../../services/taskerService";
import taskerAuth from "../../../services/taskerAuth";
import { VmReviewPanel } from "./VmReviewPanel";
import { CmReviewPanel } from "./CmReviewPanel";
import { BizFinancePanel } from "./BizFinancePanel";
import { CompliancePanel } from "./CompliancePanel";
import { SubmittedVendorDataPanel } from "./SubmittedVendorDataPanel";
import { ActivatedPanel } from "./ActivatedPanel";
import { VendorDetailChangeReviewPanel } from "./VendorDetailChangeReviewPanel";
import { useLatestFormData, useReviewerTaskType } from "./reviewerQueries";
import { resolveReviewerRoute } from "./assigneeGate";
import { stepForms } from "./taskConfig";
import { usePermissions } from "../../../context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "../../../constants/bouncerPermissions";
import {
  TransitionSuccessCard,
  type TransitionOutcome,
} from "./TransitionSuccessCard";

const VDCR_TASK_TYPE_NAME = "Vendor Detail Change Request";
function isVendorDetailChangeRequest(task: TaskerTask): boolean {
  return task.task_type_name === VDCR_TASK_TYPE_NAME;
}

export type OnTransitionSubmitted = (
  outcome: TransitionOutcome,
  message: string,
) => void;

type SubmittedInfo = { outcome: TransitionOutcome; message: string };

// TUI-G002: onboarding tasks emit BIZFIN_REVIEW; VDCR tasks emit FINANCE_REVIEW
// (different Tasker workflows, same reviewer role). Keep both keys aliased to
// the same label / title so either stage renders identically.
const REVIEWER_STAGE_LABELS: Record<string, string> = {
  VM_REVIEW: "VM Review",
  CM_REVIEW: "Category Manager Review",
  BIZFIN_REVIEW: "Business Finance Review",
  FINANCE_REVIEW: "Business Finance Review",
  COMPLIANCE_REVIEW: "Compliance Review",
};

const REVIEWER_ROLE_TITLES: Record<string, string> = {
  VM_REVIEW: "Vendor Manager",
  CM_REVIEW: "Category Manager",
  BIZFIN_REVIEW: "Biz Finance",
  FINANCE_REVIEW: "Biz Finance",
  COMPLIANCE_REVIEW: "Compliance Manager",
};

function primaryAssigneeName(task: TaskerTask): string | undefined {
  const primary =
    task.assignee_ids?.find((a) => a.role === "ASSIGNEE") ??
    task.assignee_ids?.[0];
  return primary?.name || primary?.email || undefined;
}

function ReviewerBody({
  task,
  onSubmitted,
}: {
  task: TaskerTask;
  onSubmitted: OnTransitionSubmitted;
}) {
  if (isVendorDetailChangeRequest(task)) {
    return (
      <VendorDetailChangeReviewPanel task={task} onSubmitted={onSubmitted} />
    );
  }
  switch (task.current_status) {
    case "VM_REVIEW":
      return <VmReviewPanel task={task} onSubmitted={onSubmitted} />;
    case "CM_REVIEW":
      return <CmReviewPanel task={task} onSubmitted={onSubmitted} />;
    case "BIZFIN_REVIEW":
      return <BizFinancePanel task={task} onSubmitted={onSubmitted} />;
    case "COMPLIANCE_REVIEW":
      return <CompliancePanel task={task} onSubmitted={onSubmitted} />;
    default:
      return (
        <Box sx={{ p: 4 }}>
          <Stack spacing={2}>
            <Alert severity="info">
              This task is not currently awaiting reviewer action (state:{" "}
              {task.current_status}).
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => window.history.back()}
              >
                Back
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
  }
}

export function ReviewerRouter() {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("task_id") ?? searchParams.get("taskId");
  const [submittedInfo, setSubmittedInfo] = useState<SubmittedInfo | null>(
    null,
  );
  const { hasPermission } = usePermissions();
  const authorized = hasPermission(BOUNCER_PERMISSIONS.VENDOR_ONBOARD);

  const taskQuery = useQuery({
    queryKey: ["reviewer-task", taskId],
    queryFn: () => taskerService.getTask(taskId as string, "reviewer"),
    enabled: Boolean(taskId),
    staleTime: 0,
    retry: false,
  });

  const requestData = taskQuery.data?.request_data as
    | {
        name?: string;
        email?: string;
        vendor_legal_name?: string;
        facility_display_name?: string;
      }
    | undefined;

  const taskTypeId = taskQuery.data?.task_type_id;
  const taskTypeQuery = useReviewerTaskType(taskTypeId);

  const isVdcr = taskQuery.data
    ? isVendorDetailChangeRequest(taskQuery.data)
    : false;

  const step1FormId = stepForms(taskTypeQuery.data?.task_config)?.STEP_1_BUSINESS_DETAILS;

  const step1DataQuery = useLatestFormData(
    "reviewer-step1-data",
    isVdcr ? undefined : taskId ?? undefined,
    isVdcr ? undefined : step1FormId,
  );

  const submittedLegalName =
    typeof step1DataQuery.data?.vendor_legal_name === "string"
      ? (step1DataQuery.data.vendor_legal_name as string)
      : undefined;
  const vendorName = isVdcr
    ? [requestData?.vendor_legal_name, requestData?.facility_display_name]
        .filter(Boolean)
        .join(" · ") || "Vendor"
    : submittedLegalName || requestData?.name || "Vendor";
  const status = taskQuery.data?.current_status ?? "";
  const stageLabel = useMemo(
    () => REVIEWER_STAGE_LABELS[status] ?? "",
    [status],
  );
  const roleTitle = useMemo(() => REVIEWER_ROLE_TITLES[status] ?? "", [status]);
  const assigneeName = useMemo(
    () => (taskQuery.data ? primaryAssigneeName(taskQuery.data) : undefined),
    [taskQuery.data],
  );

  // Delegate the top-level precedence to the shared resolver. Keeping the
  // switch there (not inlined here) so ReviewerRouter and
  // reviewerAccessGate.test.ts consume the same ordering — the ACTIVE-vs-
  // assignee flip has regressed twice (PR #164 r1, PR #165 r1); the test
  // now binds this exact function, so a re-flip fails the suite.
  const outcome = resolveReviewerRoute({
    authorized,
    taskId,
    taskLoading: taskQuery.isInitialLoading,
    taskErrored: taskQuery.isError,
    task: taskQuery.data ?? null,
    submitted: Boolean(submittedInfo),
    currentUserId: taskerAuth.getReviewerUserId() ?? undefined,
  });

  if (outcome === "unauthorized") {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          You don&apos;t have permission to review vendor onboarding tasks.
          Contact your admin if you believe this is a mistake.
        </Alert>
      </Box>
    );
  }

  if (outcome === "missing_task_id") {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          Missing task_id in the URL. Use the reviewer link from the invite.
        </Alert>
      </Box>
    );
  }

  if (outcome === "loading") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (outcome === "load_error") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
          <Alert severity="error">
            Failed to load task:{" "}
            {taskQuery.error instanceof Error
              ? taskQuery.error.message
              : "Unknown error"}
          </Alert>
        </Box>
      </Box>
    );
  }

  // Load succeeded — task is defined for the remaining outcomes.
  const task = taskQuery.data as TaskerTask;

  if (outcome === "submitted_overlay" && submittedInfo) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
          <TransitionSuccessCard
            outcome={submittedInfo.outcome}
            approvedMessage={submittedInfo.message}
            rejectedMessage={submittedInfo.message}
          />
        </Box>
      </Box>
    );
  }

  if (outcome === "not_assigned") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
        <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
          <Alert severity="warning">
            You are not assigned to this task.
          </Alert>
        </Box>
      </Box>
    );
  }

  if (outcome === "active_panel") {
    return <ActivatedPanel task={task} vendorLegalName={submittedLegalName} />;
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F5F7FC" }}>
      <Box sx={{ maxWidth: 1280, mx: "auto", px: 3, py: 3 }}>
        <Box
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "#E0E4EF",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              px: 4,
              py: 3,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.25,
                bgcolor: "primary.light",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StoreIcon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                {!isVdcr && task.task_visible_id && (
                  <Box component="span" sx={{ mr: 0.75 }}>
                    {task.task_visible_id} ·
                  </Box>
                )}
                {vendorName}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: 13,
                }}
              >
                {stageLabel && <span>{stageLabel}</span>}
                {assigneeName && (
                  <>
                    <span>·</span>
                    <span>{assigneeName}</span>
                  </>
                )}
                {roleTitle && (
                  <>
                    <span>·</span>
                    <span>{roleTitle}</span>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {isVdcr ? (
            <ReviewerBody
              task={task}
              onSubmitted={(o, m) => setSubmittedInfo({ outcome: o, message: m })}
            />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" },
                minHeight: 400,
              }}
            >
              <Box sx={{ borderRight: { md: 1 }, borderColor: { md: "divider" } }}>
                <SubmittedVendorDataPanel task={task} />
              </Box>
              <Box>
                <ReviewerBody
                  task={task}
                  onSubmitted={(o, m) => setSubmittedInfo({ outcome: o, message: m })}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ReviewerRouter;
